import { RefreshCw } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { PageHeader } from '@components/page-header';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Switch } from '@components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Api } from '@core/api';
import { CatalogAppDto } from '@core/api/MobileClient';
import { links } from '@core/router';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';

function getErrorMessage(error: any): string {
    return (
        error?.error?.message?.defaultMessage ??
        error?.error?.defaultMessage ??
        (error instanceof Error ? error.message : 'Unexpected error')
    );
}

const AppStore = observer(() => {
    const { mobileClient } = injectTSDI(Api);
    const navigate = useNavigate();

    const [updatingApp, setUpdatingApp] = React.useState<string | null>(null);

    const apps = useWrapRequest(
        async () =>
            (
                await mobileClient.supportApi.catalogAppSupportControllerGetCatalogApps()
            ).data,
        { defaultData: [] as CatalogAppDto[] }
    );

    const reload = () => {
        apps.request([]);
    };

    React.useEffect(reload, []);

    const setEnabled = async (app: string, enabled: boolean) => {
        setUpdatingApp(app);
        try {
            await mobileClient.supportApi.catalogAppSupportControllerUpdateCatalogApp(
                app,
                { enabled }
            );
            toast.success(`${app} ${enabled ? 'enabled' : 'disabled'}`);
        } catch (e: any) {
            toast.error(getErrorMessage(e));
        } finally {
            setUpdatingApp(null);
            reload();
        }
    };

    return (
        <>
            <PageHeader
                title="App Store"
                actions={
                    <Button
                        variant="outline"
                        loading={apps.loading}
                        onClick={reload}
                    >
                        <RefreshCw />
                        Refresh
                    </Button>
                }
            />

            <Alert variant="info" className="mb-4">
                <AlertDescription>
                    A disabled app disappears from the app store for every user
                    except those granted access to it. Open an app to grant
                    users access. Unlisted apps are enabled by the system and
                    never shown in the store.
                </AlertDescription>
            </Alert>

            <div className="w-full overflow-auto rounded-xl border bg-card">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>App</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead style={{ width: 160 }}>Listed</TableHead>
                            <TableHead style={{ width: 140 }}>
                                Enabled
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {apps.$.map((app) => (
                            <TableRow
                                key={app.app}
                                className="cursor-pointer"
                                onClick={() =>
                                    void navigate(links.appStoreApp(app.app))
                                }
                            >
                                <TableCell>
                                    <div className="flex flex-col">
                                        <span>{app.name?.en || app.app}</span>
                                        <code className="text-xs text-muted-foreground">
                                            {app.app}
                                        </code>
                                    </div>
                                </TableCell>
                                <TableCell className="capitalize">
                                    {app.category?.toLowerCase() ?? '—'}
                                </TableCell>
                                <TableCell>
                                    {app.listed ? (
                                        <Badge variant="success">Listed</Badge>
                                    ) : (
                                        <Badge variant="secondary">
                                            Unlisted
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Switch
                                        checked={Boolean(app.enabled)}
                                        disabled={updatingApp !== null}
                                        onCheckedChange={(enabled) =>
                                            setEnabled(app.app, enabled)
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </>
    );
});

export default AppStore;
